/**
 * React Hooks for Institutions
 *
 * This module provides custom hooks for managing institution state and operations
 * in React components using React Query for caching and synchronization.
 *
 * Query hooks use directApiClient for direct API calls (faster, no server action overhead).
 */

'use client';

import {
  useQuery,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import { createMutationHook } from '../mutation-utils';
import { getDependencies } from '../query-dependencies';
import {
  updateInstitutionAction,
  setInstitutionFirmContactAction,
  batchAddMatterInstitutionsAction
} from './actions';
import { discoveryKeys } from '@/lib/query-keys/discovery';
import {
  getInstitutionsCacheKey,
  getInstitutionCacheKey,
  getInstitutionTypesCacheKey,
  getMatterInstitutionsCacheKey,
  getGroupedInstitutionsCacheKey,
  getLetterTypesCacheKey
} from './utils';
import type {
  Institution,
  InstitutionTypesListResponse,
  MatterInstitutionsListResponse,
  UpdateInstitutionInput,
  GroupedInstitutionsByType,
  BatchCreateMatterInstitutionsResponse,
  LetterType,
  CreateMatterPartyRequest,
  AttachMatterContactRecipientRequest,
  CreateMatterContactRecipientRequest,
  MatterInstitution
} from './types';
import type { PaginatedResponse } from '@/lib/types/api';

// ============================================================================
// Query Hooks
// ============================================================================

/*
  **`useInstitutions` is gone** (VALE-1497).

  It fetched an unfiltered page of the catalogue, and its last caller was the
  redemption sheet's broker `<Select>` — the surface this ticket replaced with the
  server-searched picker, precisely because listing the catalogue was the defect. It
  also sent its query as `search`, which the endpoint has never read (`q` is the
  parameter), so its one filter had been silently inert.

  Reach for {@link useInstitutionSearch} (a typeahead) or `useInstitutionSearchField`
  (`lib/services/institutions/use-institution-search.ts`, the picker's own debounced,
  abortable search). Neither ships the catalogue to the client.
*/

/**
 * Typeahead search for the Discovery add-institution drawer. Hits the server-
 * side fuzzy search (`GET /institutions?q=`) — the same `q` param the inventory
 * institution picker uses — so the full catalogue is never shipped to the
 * client. Only enabled once the query is at least 2 characters. Optionally
 * scoped to an institution `type` (when a type pill is active).
 */
export function useInstitutionSearch(
  query: string,
  filters?: { type?: string },
  options?: Omit<
    UseQueryOptions<PaginatedResponse<Institution>>,
    'queryKey' | 'queryFn' | 'enabled'
  > & { enabled?: boolean }
) {
  const q = query.trim();
  const ready = q.length >= 2;
  // Pull `enabled` out of the spread so the internal `ready` guard always wins —
  // spreading `...options` last would otherwise clobber it and let a caller's
  // `enabled: true` fire a request below the 2-char minimum.
  const { enabled: callerEnabled, ...restOptions } = options ?? {};
  return useQuery({
    ...restOptions,
    queryKey: getInstitutionsCacheKey({
      q,
      type: filters?.type,
      limit: 50
    }),
    queryFn: async () => {
      const params: Record<string, string> = { q, limit: '50' };
      if (filters?.type) params.type = filters.type;
      return directApiClient.get<PaginatedResponse<Institution>>(
        '/institutions',
        {
          params
        }
      );
    },
    enabled: ready && (callerEnabled ?? true),
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook to fetch a single institution
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useInstitution(
  id: number,
  options?: Omit<UseQueryOptions<Institution>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: getInstitutionCacheKey(id),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<Institution>(`/institutions/${id}`);
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!id,
    ...options
  });
}

/**
 * Hook to fetch all institution types
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useInstitutionTypes(
  options?: Omit<
    UseQueryOptions<InstitutionTypesListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getInstitutionTypesCacheKey(),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<InstitutionTypesListResponse>(
        '/institution-types'
      );
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (reference data)
    ...options
  });
}

/**
 * Hook to fetch institutions linked to a matter
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useMatterInstitutions(
  matterId: number,
  options?: Omit<
    UseQueryOptions<MatterInstitutionsListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getMatterInstitutionsCacheKey(matterId),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<MatterInstitutionsListResponse>(
        `/matters/${matterId}/institutions`
      );
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!matterId && !isNaN(matterId),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    ...options
  });
}

/**
 * Hook to fetch notifiable global institutions grouped by type (popular-first),
 * feeding the Discovery add-institution drawer's type pills + popular tiles +
 * search. Reference data — cached for 30 minutes.
 */
export function useGroupedInstitutions(
  options?: Omit<
    UseQueryOptions<GroupedInstitutionsByType[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getGroupedInstitutionsCacheKey(),
    queryFn: async () => {
      return directApiClient.get<GroupedInstitutionsByType[]>(
        '/institutions/grouped'
      );
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (reference data)
    ...options
  });
}

/**
 * The letter types a solicitor may choose — what the firm is ASKING a party,
 * which is what selects the letter that gets written.
 *
 * Fetched rather than hard-coded: the same registry decides which template the
 * backend renders, so a label defined twice would eventually describe a letter
 * it no longer produces. Reference data, cached like the grouped listing.
 */
export function useLetterTypes(
  options?: Omit<UseQueryOptions<LetterType[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: getLetterTypesCacheKey(),
    queryFn: async () => {
      return directApiClient.get<LetterType[]>('/institutions/letter-types');
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (reference data)
    ...options
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for updating an institution (takes institutionId as parameter).
 * Used from the Communications composer's inline add-email affordance on
 * custom-institution threads (mirrors `useGenericUpdateContact`). Only firm-
 * owned (custom) institutions can be updated — the backend enforces this via
 * the `LawFirmAuthGuard` + ownership check on `PUT /institutions/:id`.
 *
 * `matterId` is required for invalidation: editing the institution email
 * needs to flip the corresponding Communications thread header from
 * `composeBlockedReason='no-email'` to composable.
 */
export function useUpdateInstitution(
  matterId: number,
  options?: UseMutationOptions<
    void,
    Error,
    { institutionId: number; data: UpdateInstitutionInput }
  >
) {
  const useHook = createMutationHook<
    void,
    { institutionId: number; data: UpdateInstitutionInput }
  >({
    mutationFn: async ({ institutionId, data }) => {
      const result = await updateInstitutionAction(institutionId, data);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update institution');
      }
    },
    invalidateKeys: getDependencies('institution', matterId),
    refetchKeys: getDependencies('institution', matterId),
    onSuccessMessage: () => 'Institution updated successfully',
    onErrorMessage: 'Failed to update institution'
  });

  return useHook(options);
}

/**
 * Hook for setting (or clearing) the firm's own contact email on a **global**
 * institution — the per-firm overlay that lets a firm address a specialist
 * broker's own account manager without touching the Vale-wide institution row
 * every other firm shares.
 *
 * Distinct from `useUpdateInstitution`: that edits a firm-owned (custom)
 * institution in place. This one never modifies the institution — it writes
 * `law_firms.institution_contacts`, and passing `email: null` removes the
 * override so correspondence falls back to the global address.
 *
 * `matterId` is required for invalidation: the Communications thread header
 * renders the resolved address, its `firm`/`global` source marker, and the
 * email-research notice, all of which change on write.
 */
export function useSetInstitutionFirmContact(
  matterId: number,
  options?: UseMutationOptions<
    void,
    Error,
    { institutionId: number; email: string | null; contactName?: string | null }
  >
) {
  const useHook = createMutationHook<
    void,
    { institutionId: number; email: string | null; contactName?: string | null }
  >({
    mutationFn: async ({ institutionId, email, contactName }) => {
      const result = await setInstitutionFirmContactAction(
        institutionId,
        email,
        contactName
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to save the institution email');
      }
    },
    invalidateKeys: getDependencies('institution', matterId),
    refetchKeys: getDependencies('institution', matterId),
    onSuccessMessage: (_data, variables) =>
      variables.email === null
        ? 'Reverted to the default institution email'
        : 'Institution email saved for your firm',
    onErrorMessage: 'Failed to save the institution email'
  });

  return useHook(options);
}

/**
 * Hook to batch-add institutions to a matter from the Discovery drawer.
 * Create-or-restore per id; already-linked ids are skipped server-side, so a
 * partially-overlapping selection never errors. Invalidates the institution +
 * discovery surfaces so the newly-linked institutions appear.
 */
export function useBatchAddInstitutions(
  matterId: number,
  options?: UseMutationOptions<
    BatchCreateMatterInstitutionsResponse,
    Error,
    { institutionIds: number[] }
  >
) {
  const useHook = createMutationHook<
    BatchCreateMatterInstitutionsResponse,
    { institutionIds: number[] }
  >({
    mutationFn: async ({ institutionIds }) => {
      const result = await batchAddMatterInstitutionsAction(
        matterId,
        institutionIds
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to add institutions');
      }
      return result.data;
    },
    invalidateKeys: [
      ...getDependencies('institution', matterId),
      discoveryKeys.forMatter(matterId)
    ],
    refetchKeys: [
      getMatterInstitutionsCacheKey(matterId),
      discoveryKeys.forMatter(matterId)
    ],
    onSuccessMessage: (data) => {
      const added = data.results.filter((r) => r.outcome !== 'skipped').length;
      if (added === 0) {
        return 'No new institutions added, already on this matter';
      }
      return `${added} institution${added !== 1 ? 's' : ''} added`;
    },
    onErrorMessage: 'Failed to add institutions'
  });

  return useHook(options);
}

/**
 * Hook to create a party that is not in the catalogue and attach it to the
 * matter, in one action, from the Discovery drawer.
 *
 * This is the affordance that removes the dead end. Before it, a solicitor who
 * could not find an organisation had no way forward from Discovery at all, so
 * they went to the Inventory tab and invented an asset purely to reach the
 * organisation picker — which is how a childcare provider ended up recorded as
 * a generic "other" institution and received a bank's question set.
 *
 * One call, not two, because the institution and its attachment to the matter
 * are a single decision: creating the first and failing the second would leave
 * a firm-scoped organisation attached to nothing.
 */
export function useCreateMatterParty(
  matterId: number,
  options?: UseMutationOptions<
    MatterInstitution,
    Error,
    CreateMatterPartyRequest
  >
) {
  const useHook = createMutationHook<
    MatterInstitution,
    CreateMatterPartyRequest
  >({
    mutationFn: async (body) => {
      return directApiClient.post<MatterInstitution>(
        `/matters/${matterId}/institutions/party`,
        body
      );
    },
    invalidateKeys: [
      ...getDependencies('institution', matterId),
      discoveryKeys.forMatter(matterId)
    ],
    refetchKeys: [
      getMatterInstitutionsCacheKey(matterId),
      discoveryKeys.forMatter(matterId)
    ],
    onSuccessMessage: (data) => `${data.institution?.name ?? 'Party'} added`,
    onErrorMessage: 'Failed to add this party'
  });

  return useHook(options);
}

/**
 * Add a PERSON as a letter recipient on the matter.
 *
 * The sibling of `useCreateMatterParty`, and the reason the drawer asks which
 * kind of recipient is being recorded. A person becomes a contact on the matter
 * carrying a letter type, not a firm-scoped institution — which is what stops
 * the deceased's accountant being addressed as "The Manager".
 *
 * Reuses the existing matter-contact create endpoint rather than adding a
 * parallel one: contact creation already owns name handling, dedupe and the
 * matter link, and a second route would be a second definition of all three.
 */
/**
 * Make a contact ALREADY on the matter a letter recipient, by recording what
 * the firm is asking them.
 *
 * The third door into Discovery, beside `useCreateMatterParty` (an
 * organisation) and `useCreateMatterContactRecipient` (a brand-new person).
 * Without it the drawer could only ever create: a lawyer who had already
 * recorded the deceased's accountant on the Contacts tab had no way to write to
 * them except to type the name again, which mints a second contact for one
 * person on their own estate.
 *
 * `letterType` is the whole payload. `NULL` on `contact_matters` means "not a
 * letter recipient", so setting it is precisely the act of making them one —
 * there is no separate attach step and no second row to create.
 */
export function useAttachMatterContactRecipient(
  matterId: number,
  options?: UseMutationOptions<
    unknown,
    Error,
    AttachMatterContactRecipientRequest
  >
) {
  const useHook = createMutationHook<
    unknown,
    AttachMatterContactRecipientRequest
  >({
    mutationFn: async ({ contactId, letterType }) => {
      return directApiClient.put(`/matters/${matterId}/contacts/${contactId}`, {
        letterType
      });
    },
    invalidateKeys: [
      ...getDependencies('contact', matterId),
      discoveryKeys.forMatter(matterId)
    ],
    refetchKeys: [discoveryKeys.forMatter(matterId)],
    onSuccessMessage: () => 'Recipient added',
    onErrorMessage: 'Failed to add this recipient'
  });

  return useHook(options);
}

export function useCreateMatterContactRecipient(
  matterId: number,
  options?: UseMutationOptions<
    { id: number },
    Error,
    CreateMatterContactRecipientRequest
  >
) {
  const useHook = createMutationHook<
    { id: number },
    CreateMatterContactRecipientRequest
  >({
    mutationFn: async (body) => {
      return directApiClient.post<{ id: number }>(
        `/matters/${matterId}/contacts`,
        body
      );
    },
    invalidateKeys: [
      ...getDependencies('contact', matterId),
      discoveryKeys.forMatter(matterId)
    ],
    refetchKeys: [discoveryKeys.forMatter(matterId)],
    onSuccessMessage: () => 'Recipient added',
    onErrorMessage: 'Failed to add this recipient'
  });

  return useHook(options);
}
